import { defineStore } from 'pinia'
import { debug } from '@/composables/useLogger'

interface ImageEmbedding {
  source: string
  text: string
}

interface EmbeddingsState {
  embeddings: Record<string, ImageEmbedding[]>
  loading: Record<string, boolean>
  error: Record<string, string | null>
}

interface FetchEmbeddingsResponse {
  success: boolean
  error?: string
  embeddings?: ImageEmbedding[]
}

export const useEmbeddingsStore = defineStore('embeddings', {
  state: (): EmbeddingsState => ({
    embeddings: {},
    loading: {},
    error: {},
  }),

  actions: {
    async fetchEmbeddings(bubbleId: string): Promise<FetchEmbeddingsResponse> {
      if (this.loading[bubbleId]) {
        return { success: false, error: 'Already loading embeddings' }
      }

      this.loading[bubbleId] = true
      this.error[bubbleId] = null

      try {
        const response = await fetch(
          `${useRuntimeConfig().public.apiBase}/bubble/${bubbleId}/image_embeddings`
        )

        if (response.ok) {
          const data = await response.json()
          debug('🔍 Embeddings API Response:', {
            bubbleId,
            embeddings: data.imageEmbeddings,
            count: data.imageEmbeddings?.length,
          })
          this.embeddings[bubbleId] = data.imageEmbeddings
          return {
            success: true,
            embeddings: data.imageEmbeddings,
          }
        } else {
          const errorData = await response.json()
          const errorMessage = errorData.detail || 'Failed to fetch embeddings'
          this.error[bubbleId] = errorMessage
          return {
            success: false,
            error: errorMessage,
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch embeddings'
        debug('Failed to fetch embeddings:', error)
        this.error[bubbleId] = errorMessage
        return {
          success: false,
          error: errorMessage,
        }
      } finally {
        this.loading[bubbleId] = false
      }
    },

    getEmbeddingForImage(bubbleId: string, imagePath: string): string | null {
      const embeddings = this.embeddings[bubbleId]
      debug('🔍 Getting embedding for image:', {
        bubbleId,
        imagePath,
        hasEmbeddings: !!embeddings,
        embeddingsCount: embeddings?.length ?? 0,
        availableSources: embeddings?.map((e) => e.source),
      })

      if (!embeddings) return null

      // Remove any leading URL or datastore path if present
      const cleanImagePath = imagePath.replace(
        /^(https?:\/\/[^/]+)?.*?(\/data\/offline\/)/,
        '/data/offline/'
      )
      debug('🔍 Clean image path:', {
        original: imagePath,
        cleaned: cleanImagePath,
        availableSources: embeddings.map((e) => e.source),
      })

      // Try to find exact match first
      let embedding = embeddings.find((e) => e.source === cleanImagePath)
      debug('🔍 Exact match result:', {
        found: !!embedding,
        matchedPath: embedding?.source,
      })

      // If no exact match, try matching just the filename
      if (!embedding) {
        const imageFilename = cleanImagePath.split('/').pop()
        debug('🔍 Trying filename match:', {
          filename: imageFilename,
          availablePaths: embeddings.map((e) => e.source),
        })
        embedding = embeddings.find((e) => e.source.endsWith('/' + imageFilename))
        debug('🔍 Filename match result:', {
          found: !!embedding,
          matchedPath: embedding?.source,
        })
      }

      return embedding?.text ?? null
    },

    clearEmbeddings(bubbleId: string) {
      this.embeddings[bubbleId] = []
      this.loading[bubbleId] = false
      this.error[bubbleId] = null
    },
  },
})
